import { Drawer } from "src/global/ui/Drawer/Drawer.tsx"
import { BodyNormal, Heading2 } from "src/global/ui/typography/Text/Text"
import { Flex, Row, AlertProps } from "antd"
import { theme } from "src/global/ui/themes/theme.ts"
import { Button } from "src/global/ui/Button/Button"
import { Icon } from "src/global/ui/Icon/Icon"
import { graphql } from "src/api/graphql/gql"
import { ApolloError, useApolloClient, useMutation, useQuery } from "@apollo/client"
import {
  DraftExpectationsList,
  ExpectationDraftConfigMaskedFragment,
} from "src/pages/DataAssets/views/Expectations/DraftExpectationsList/DraftExpectationsList"
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { ExpectationsTab_GetExpectationsDocument } from "src/pages/DataAssets/views/Expectations/SimpleExpectationsTab"
import { AlertBanner } from "src/global/ui/Alert/AlertBanner"
import { useExpectAiSurveyModal } from "src/global/ui/Surveys/useExpectAiSurveyModal"
import { BetaTag } from "src/global/ui/Tag/BetaTag.tsx"
import { GenerateExpectationsButtonDocument } from "src/pages/DataAssets/views/Expectations/GenerateExpectationsButton/GenerateExpectationsButton"
import { formatLocalCalendarDateWithTime } from "src/global/utils/formatTime"

export const SUB_HEADER_TEXT_BASE =
  "ExpectAl has analyzed your data and designed the following tests. The tests are structured to provide meaningful insights and validate key aspects of your dataset. Tests are sometimes based on anomalies detected in the data, so they may fail on the first validation to bring your attention to potential problems."

export const SUB_HEADER_ALERT_BASE = "Approve or reject recommended Expectations before"

const SUB_HEADER_ALERT_AFTER = "After that, the recommendations will be discarded."

export const Footer = () => {
  return <Drawer.FooterButton type="default">Save</Drawer.FooterButton>
}

type DraftExpectationsApprovalButtonWrapperProps = {
  onClick: () => void
  assetID: string
  children: (props: { canDraftConfigs: boolean; onClick: () => void; isDrawerOpen: boolean }) => ReactNode
}

export const getExpectationDraftConfigsDocument = graphql(`
  query getExpectationDraftConfigs($input: ExpectationDraftConfigsInput!) {
    expectationDraftConfigs(input: $input) {
      id
      createdAt
      updatedAt
      expiry
      draftExpectation {
        ...ExpectationDraftConfigMasked
      }
    }
  }
`)

export const approveExpectationDraftConfigMutation = graphql(`
  mutation approveExpectationDraftConfig($input: ApproveExpectationDraftConfigsInput!) {
    approveExpectationDraftConfigs(input: $input) {
      expectations {
        description
        expectationType
        kwargs
      }
    }
  }
`)

export const deleteExpectationDraftConfigMutation = graphql(`
  mutation deleteExpectationDraftConfig($input: DeleteExpectationDraftConfigsInput!) {
    deleteExpectationDraftConfigs(input: $input) {
      deletedExpectationDraftConfigIds
    }
  }
`)

export const DraftExpectationsApprovalButtonWrapper = ({
  onClick,
  assetID,
  children,
}: DraftExpectationsApprovalButtonWrapperProps) => {
  const { openModalAfterDelay } = useExpectAiSurveyModal()
  const client = useApolloClient()
  const [open, setOpen] = useState<boolean | null>(() => null)
  const [alert, setAlert] = useState<AlertProps>()
  const { data, loading } = useQuery(getExpectationDraftConfigsDocument, {
    variables: {
      input: {
        assetId: assetID,
      },
    },
    skip: !assetID,
  })

  const [approveExpectationDraftConfigs, { loading: approveLoading }] = useMutation(
    approveExpectationDraftConfigMutation,
    {
      refetchQueries: [
        ExpectationsTab_GetExpectationsDocument,
        getExpectationDraftConfigsDocument,
        GenerateExpectationsButtonDocument,
      ],
      onError: (error) => {
        handleError("Failed to approve Expectation(s)", error)
      },
    },
  )

  const [deleteExpectationDraftConfigs, { loading: deleteLoading }] = useMutation(
    deleteExpectationDraftConfigMutation,
    {
      onError: (error) => {
        handleError("Failed to reject Expectation(s)", error)
      },
    },
  )

  const handleClose = useCallback(() => {
    openModalAfterDelay()
    setOpen(false)
  }, [openModalAfterDelay])

  const expectationDraftConfigsPendingApproval = Boolean(data?.expectationDraftConfigs?.length)

  useEffect(() => {
    /*
     *  Open Drawer on render or when draft configs are pending approval.
     */
    if (expectationDraftConfigsPendingApproval) {
      setOpen((previousValue) => (previousValue === null ? true : previousValue))
    }
  }, [expectationDraftConfigsPendingApproval])

  useEffect(() => {
    /*
     *  Close Drawer when no draft configs are pending approval.
     */
    if (open && data?.expectationDraftConfigs?.length === 0) {
      handleClose()
    }
  }, [data?.expectationDraftConfigs?.length, handleClose, open])

  const timestamp = data?.expectationDraftConfigs?.[0]?.expiry ?? ""

  const ExpectationDraftConfigIdDraftExpectationTuple =
    data?.expectationDraftConfigs?.map<[string, ExpectationDraftConfigMaskedFragment]>((draftConfig) => [
      draftConfig.id,
      draftConfig.draftExpectation,
    ]) ?? []

  const onButtonClick = () => {
    if (expectationDraftConfigsPendingApproval) {
      setOpen(true)
    } else {
      onClick()
    }
  }

  const handleError = (message: string, error: ApolloError) => {
    setAlert({
      message,
      description: error.message,
    })
  }

  const handleApprove = async (id: string) => {
    await approveExpectationDraftConfigs({ variables: { input: { expectationDraftConfigIds: [id] } } })
    client.cache.evict({ id: `ExpectationDraftConfig:${id}` })
  }

  const handleReject = async (id: string) => {
    await deleteExpectationDraftConfigs({ variables: { input: { expectationDraftConfigIds: [id] } } })
    client.cache.evict({ id: `ExpectationDraftConfig:${id}` })
  }

  const handleApproveAll = async () => {
    const configIds = data?.expectationDraftConfigs?.map((draftConfig) => draftConfig.id) || []
    if (configIds.length > 0) {
      await approveExpectationDraftConfigs({ variables: { input: { expectationDraftConfigIds: configIds } } })
      configIds.forEach((id) => {
        client.cache.evict({ id: `ExpectationDraftConfig:${id}` })
      })
    }
  }

  const canDraftConfigs = useMemo(
    () => expectationDraftConfigsPendingApproval,
    [expectationDraftConfigsPendingApproval],
  )

  return (
    <>
      <Drawer
        title={
          <Flex gap={theme.spacing.xs}>
            <Heading2>Recommended Expectations</Heading2>
            <BetaTag />
          </Flex>
        }
        placement="right"
        footer={
          <Drawer.Footer>
            <Button type="primary" size="large" onClick={handleApproveAll}>
              <Flex gap={theme.spacing.xxs} align="center">
                <Icon name="check" lineHeight={theme.spacing.xs} size={theme.spacing.xs} />
                Approve all
              </Flex>
            </Button>
          </Drawer.Footer>
        }
        destroyOnClose
        open={!!open}
        onClose={handleClose}
      >
        <BodyNormal>{SUB_HEADER_TEXT_BASE}</BodyNormal>
        <BodyNormal>
          <Flex align="center" style={{ marginTop: theme.spacing.s }}>
            <div style={{ flex: 2 }}>
              <Icon name="warning" color="orange" />
            </div>
            <div style={{ flex: 22 }}>
              {SUB_HEADER_ALERT_BASE} <b>{formatLocalCalendarDateWithTime(timestamp)}</b>. {SUB_HEADER_ALERT_AFTER}
            </div>
          </Flex>
        </BodyNormal>
        <Row style={{ marginTop: theme.spacing.m }}>
          <DraftExpectationsList
            ExpectationDraftConfigIdDraftExpectationTuple={ExpectationDraftConfigIdDraftExpectationTuple}
            onApprove={handleApprove}
            onReject={handleReject}
            loading={loading || approveLoading || deleteLoading}
          />
        </Row>
        {alert && (
          <AlertBanner
            message={alert.message}
            description={alert.description}
            type="error"
            closable={true}
            onClose={() => setAlert(undefined)}
          />
        )}
      </Drawer>
      {children({
        canDraftConfigs,
        onClick: onButtonClick,
        isDrawerOpen: !!open,
      })}
    </>
  )
}
